/**
 * Pure helpers for the schedule views (Phase 9A). Dates are agency-local 'YYYY-MM-DD' strings
 * handled as UTC calendar days, so no browser timezone can shift them (the old UI used
 * toISOString().split('T')[0] and could land on the wrong day).
 */
import type {
  CreateShiftRequest, Discipline, RecurrenceRule, ScheduleWarning, Shift, ShiftVisitType, WarningCode,
} from '../../dtos/scheduling/schedule.dto';

const DAY_MS = 86400000;
const toMs = (date: string) => {
  const [y, m, d] = date.split('-').map(Number);
  return Date.UTC(y, m - 1, d);
};
const fromMs = (ms: number) => new Date(ms).toISOString().slice(0, 10);

export const addDays = (date: string, days: number): string => fromMs(toMs(date) + days * DAY_MS);
export const dayOfWeek = (date: string): number => new Date(toMs(date)).getUTCDay();

/** Monday of the week containing `date`. */
export const weekStart = (date: string): string => {
  const dow = dayOfWeek(date);
  return addDays(date, dow === 0 ? -6 : 1 - dow);
};

export const weekDays = (start: string): string[] => Array.from({ length: 7 }, (_, i) => addDays(start, i));

/** 6 Monday-start weeks covering the month of `date` (42 days). */
export const monthGrid = (date: string): string[] => {
  const first = `${date.slice(0, 7)}-01`;
  const start = weekStart(first);
  return Array.from({ length: 42 }, (_, i) => addDays(start, i));
};

export const addMonths = (date: string, months: number): string => {
  const [y, m] = date.split('-').map(Number);
  const d = new Date(Date.UTC(y, m - 1 + months, 1));
  return d.toISOString().slice(0, 10);
};

const WEEKDAY = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTH = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export const weekdayLabel = (date: string): string => WEEKDAY[dayOfWeek(date)];
export const dayLabel = (date: string): string => `${WEEKDAY[dayOfWeek(date)]} ${Number(date.slice(8, 10))}`;
export const longDateLabel = (date: string): string => `${WEEKDAY[dayOfWeek(date)]}, ${MONTH[Number(date.slice(5, 7)) - 1]} ${Number(date.slice(8, 10))}`;
export const monthLabel = (date: string): string => `${MONTH[Number(date.slice(5, 7)) - 1]} ${date.slice(0, 4)}`;
export const rangeLabel = (from: string, to: string): string => (from.slice(0, 7) === to.slice(0, 7)
  ? `${MONTH[Number(from.slice(5, 7)) - 1]} ${Number(from.slice(8, 10))}–${Number(to.slice(8, 10))}, ${from.slice(0, 4)}`
  : `${MONTH[Number(from.slice(5, 7)) - 1]} ${Number(from.slice(8, 10))} – ${MONTH[Number(to.slice(5, 7)) - 1]} ${Number(to.slice(8, 10))}, ${to.slice(0, 4)}`);

/** '19:00' → '7:00 PM'; '07:30' → '7:30 AM'. */
export const formatTime = (time: string | null | undefined): string => {
  if (!time || !/^\d{1,2}:\d{2}/.test(time)) return '';
  const [h, m] = time.split(':').map(Number);
  const suffix = h >= 12 ? 'PM' : 'AM';
  return `${((h + 11) % 12) + 1}:${String(m).padStart(2, '0')} ${suffix}`;
};

/** Compact window: '7a–7p', '10:30p–6a'. */
export const shortTime = (time: string): string => {
  const [h, m] = time.split(':').map(Number);
  return `${((h + 11) % 12) + 1}${m ? `:${String(m).padStart(2, '0')}` : ''}${h >= 12 ? 'p' : 'a'}`;
};

export const shiftWindow = (shift: Pick<Shift, 'startTime' | 'endTime' | 'overnight'>, compact = false): string => {
  const f = compact ? shortTime : formatTime;
  return `${f(shift.startTime)}–${f(shift.endTime)}${shift.overnight ? ' (+1)' : ''}`;
};

/** 720 → '12 h'; 690 → '11.5 h'; 45 → '45 min'. */
export const formatMinutes = (minutes: number | null | undefined): string => {
  const m = Math.max(0, Math.round(minutes || 0));
  if (m < 60) return `${m} min`;
  const h = Math.round((m / 60) * 10) / 10;
  return `${h} h`;
};

/** Minutes between two 'HH:mm' times, overnight when end ≤ start (wall clock, ignores DST). */
export const windowMinutes = (start: string, end: string): number => {
  const [sh, sm] = start.split(':').map(Number);
  const [eh, em] = end.split(':').map(Number);
  const s = sh * 60 + sm;
  const e = eh * 60 + em;
  return e > s ? e - s : e + 1440 - s;
};

export const addMinutesToTime = (time: string, minutes: number): string => {
  const [h, m] = time.split(':').map(Number);
  const total = (((h * 60 + m + minutes) % 1440) + 1440) % 1440;
  return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
};

/** Common PDN and visit windows (the ShiftDrawer's one-tap presets). */
export const TIME_PRESETS: { id: string; label: string; startTime: string; endTime: string }[] = [
  { id: 'day12', label: 'Day 7a–7p', startTime: '07:00', endTime: '19:00' },
  { id: 'night12', label: 'Night 7p–7a', startTime: '19:00', endTime: '07:00' },
  { id: 'night8', label: 'Night 10p–6a', startTime: '22:00', endTime: '06:00' },
  { id: 'day8', label: 'Day 8a–4p', startTime: '08:00', endTime: '16:00' },
  { id: 'visit1', label: '1 h visit', startTime: '09:00', endTime: '10:00' },
];

export type RecurrencePresetId = 'none' | 'daily' | 'weekdays' | 'weekends' | 'custom';

export const RECURRENCE_PRESETS: { id: RecurrencePresetId; label: string }[] = [
  { id: 'none', label: 'Does not repeat' },
  { id: 'daily', label: 'Every day' },
  { id: 'weekdays', label: 'Mon–Fri' },
  { id: 'weekends', label: 'Sat–Sun' },
  { id: 'custom', label: 'Custom days' },
];

export type RecurrenceEnd = { kind: 'until'; until: string } | { kind: 'count'; count: number };

/** The API rule for a preset, or null for "Does not repeat". */
export const buildRecurrence = (
  preset: RecurrencePresetId,
  end: RecurrenceEnd,
  { customDays = [], interval = 1 }: { customDays?: number[]; interval?: number } = {}
): RecurrenceRule | null => {
  if (preset === 'none') return null;
  const ending = end.kind === 'until' ? { until: end.until, count: null } : { count: end.count, until: null };
  if (preset === 'daily') return { freq: 'daily', interval: 1, ...ending };
  const byDays = preset === 'weekdays' ? [1, 2, 3, 4, 5] : preset === 'weekends' ? [0, 6] : [...customDays].sort();
  return { freq: 'weekly', interval: Math.max(1, interval), byDays, ...ending };
};

export interface ShiftFormState {
  patientId: string;
  providerId: string | null;
  discipline: Discipline | '';
  visitType: ShiftVisitType;
  date: string;
  startTime: string;
  endTime: string;
  recurrence: RecurrencePresetId;
  customDays: number[];
  interval: number;
  end: RecurrenceEnd;
  notes: string;
  authId: string | null;
}

export const emptyShiftForm = (date: string, overrides: Partial<ShiftFormState> = {}): ShiftFormState => ({
  patientId: '',
  providerId: null,
  discipline: '',
  visitType: 'Routine',
  date,
  startTime: '07:00',
  endTime: '19:00',
  recurrence: 'none',
  customDays: [dayOfWeek(date)],
  interval: 1,
  end: { kind: 'until', until: addDays(date, 27) },
  notes: '',
  authId: null,
  ...overrides,
});

/** Form → API request. Title is left to the server ("RN · Routine"). */
export const toCreateRequest = (form: ShiftFormState, extra: Partial<CreateShiftRequest> = {}): CreateShiftRequest => ({
  patientId: form.patientId,
  providerId: form.providerId || null,
  ...(form.discipline ? { discipline: form.discipline } : {}),
  visitType: form.visitType,
  date: form.date,
  startTime: form.startTime,
  endTime: form.endTime,
  recurrence: buildRecurrence(form.recurrence, form.end, { customDays: form.customDays, interval: form.interval }),
  authId: form.authId,
  notes: form.notes || undefined,
  ...extra,
});

/** Required fields the coordinator still has to fill (in order). */
export const missingFields = (form: ShiftFormState): string[] => {
  const missing: string[] = [];
  if (!form.patientId) missing.push('patient');
  if (!form.providerId && !form.discipline) missing.push('clinician or discipline');
  if (!form.date) missing.push('date');
  if (form.recurrence === 'custom' && form.customDays.length === 0) missing.push('repeat days');
  if (form.recurrence !== 'none' && form.end.kind === 'until' && form.end.until < form.date) missing.push('end date');
  return missing;
};

/** Shifts shown on `date`: those that start that day, plus overnight shifts ending that day (SCH-03). */
export const shiftsOnDay = (shifts: Shift[], date: string): Shift[] =>
  shifts.filter((s) => s.date === date || (s.overnight && s.endDate === date))
    .sort((a, b) => (a.date === b.date ? a.startTime.localeCompare(b.startTime) : a.date.localeCompare(b.date)));

/** True when `shift` is shown on `date` as the tail of the previous night. */
export const isCarryOver = (shift: Shift, date: string): boolean => shift.overnight && shift.date !== date && shift.endDate === date;

export const isCountedShift = (s: Pick<Shift, 'status'>): boolean => s.status !== 'CANCELLED' && s.status !== 'NO_SHOW';

export interface ClinicianWeekRow {
  providerId: string;
  name: string;
  discipline: string;
  minutes: number;
  days: Record<string, Shift[]>;
}

/** Rows for the clinician week: one per clinician, plus "Unstaffed" (providerId ''). */
export const clinicianWeekRows = (shifts: Shift[], days: string[]): ClinicianWeekRow[] => {
  const rows = new Map<string, ClinicianWeekRow>();
  for (const s of shifts) {
    const key = s.providerId || '';
    if (!rows.has(key)) {
      rows.set(key, {
        providerId: key,
        name: s.provider?.name || (key ? 'Clinician' : 'Unstaffed'),
        discipline: s.provider?.discipline || s.discipline,
        minutes: 0,
        days: Object.fromEntries(days.map((d) => [d, []])),
      });
    }
    const row = rows.get(key)!;
    if (row.days[s.date]) row.days[s.date].push(s);
    if (isCountedShift(s) && days.includes(s.date)) row.minutes += s.durationMinutes || 0;
  }
  return [...rows.values()].sort((a, b) => (a.providerId === '' ? -1 : b.providerId === '' ? 1 : a.name.localeCompare(b.name)));
};

/** Tab-separated row for pasting into HHAX: ref, clinician code, date, start, end. */
export const hhaxCopyText = (shift: Shift): string =>
  [shift.scheduleRef || '', shift.provider?.clinicianCode || '', shift.date, shift.startTime, shift.endTime].join('\t');

/** Short heading and a hint on how to fix it, per warning code. */
export const WARNING_COPY: Record<WarningCode, { title: string; hint?: string }> = {
  SOC_INCOMPLETE: { title: 'Start of care not complete', hint: 'You can still schedule; finish the SOC checklist before the visit.' },
  PROVIDER_OVERLAP: { title: 'Clinician double-booked', hint: 'Pick another clinician or change the time.' },
  PATIENT_OVERLAP: { title: 'Overlaps another shift for this patient', hint: 'Overlap up to the handoff window is fine.' },
  LICENSE_EXPIRED: { title: 'License expired', hint: 'Update the clinician\'s license in User management.' },
  LICENSE_EXPIRING: { title: 'License expiring soon' },
  DISCIPLINE_MISMATCH: { title: 'Discipline mismatch', hint: 'Initial and evaluation nursing visits need an RN.' },
  NOT_ON_CARE_TEAM: { title: 'Not on the care team', hint: 'Keep "Add to care team" checked so they can open the chart.' },
  NO_PA_COVERS_DATE: { title: 'No approved PA covers some dates', hint: 'The visits will be unlinked unless a PA is approved.' },
  PA_OVERSCHEDULED: { title: 'More than the PA allows', hint: 'Scheduled shifts would use more units than approved.' },
  PA_WEEKLY_EXCEEDED: { title: 'Over the weekly PA amount' },
  BEYOND_PA_END: { title: 'Runs past the PA end date' },
  ASSISTANT_SUPERVISOR_MISSING: { title: 'No supervising therapist', hint: 'Add a licensed therapist of the same discipline to the care team; an assistant works under their supervision.' },
  VISIT_OVER_PER_VISIT_LIMIT: { title: 'Longer than one billable visit', hint: 'Skilled nursing and aide visits are billed per visit of up to 2.5 hours. Longer nursing care needs PDN.' },
  SN_VISITS_PER_DAY: { title: 'More than 3 skilled nursing visits that day', hint: 'Only 3 skilled nursing visits a day are billed.' },
  PDN_SN_SAME_DAY: { title: 'PDN and a skilled nursing visit the same day', hint: 'They must not overlap, and each needs its own approved PA.' },
  HOSPICE_ELECTED: { title: 'Hospice elected', hint: 'Care related to the terminal condition may be the hospice’s responsibility. Check before scheduling.' },
  PPECC_ATTENDANCE_DAY: { title: 'PPECC attendance day', hint: 'Schedule PDN outside the hours the patient is at the centre.' },
};

export const needsConfirmation = (warnings: ScheduleWarning[]): boolean => warnings.some((w) => w.severity !== 'info');

/** Shown with each warning: the first few dates it applies to. */
export const warningDates = (w: ScheduleWarning): string => {
  if (!w.dates?.length) return '';
  const shown = w.dates.slice(0, 3).map(dayLabel).join(', ');
  const more = (w.count || w.dates.length) - Math.min(3, w.dates.length);
  return more > 0 ? `${shown} +${more} more` : shown;
};
